import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { branding } = useBranding();
    const [status, setStatus] = useState<"verifying" | "success" | "error">("verifying");
    const [message, setMessage] = useState("");

    const token = searchParams.get("token");

    useEffect(() => {
        if (!token) {
            setStatus("error");
            setMessage("Invalid verification link. Token is missing.");
            return;
        }

        const verifyEmail = async () => {
            try {
                const response = await fetch("/api/auth/verify-email/", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({ token }),
                });

                const data = await response.json();

                if (response.ok) {
                    setStatus("success");
                    setMessage(data.message || "Email verified successfully!");
                } else {
                    setStatus("error");
                    setMessage(data.detail || "Failed to verify email. The link may have expired.");
                }
            } catch (error) {
                console.error("Verification error:", error);
                setStatus("error");
                setMessage("An error occurred while verifying your email. Please try again.");
            }
        };

        verifyEmail();
    }, [token]);

    const primaryColor = branding?.branding?.colors?.primary || "#1d3658";

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
            <Card className="w-full max-w-md p-8">
                <div className="text-center">
                    {status === "verifying" && (
                        <>
                            <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: primaryColor }} />
                            <h1 className="text-2xl font-bold mb-2">Verifying Your Email</h1>
                            <p className="text-slate-600">Please wait while we verify your email address...</p>
                        </>
                    )}

                    {status === "success" && (
                        <>
                            <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: primaryColor }} />
                            <h1 className="text-2xl font-bold mb-2" style={{ color: primaryColor }}>
                                Email Verified!
                            </h1>
                            <p className="text-slate-600 mb-6">{message}</p>
                            <Button
                                onClick={() => navigate("/login")}
                                className="w-full"
                                style={{ backgroundColor: primaryColor }}
                            >
                                Continue to Login
                            </Button>
                        </>
                    )}

                    {status === "error" && (
                        <>
                            <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
                            <h1 className="text-2xl font-bold mb-2 text-red-600">Verification Failed</h1>
                            <p className="text-slate-600 mb-6">{message}</p>
                            <div className="space-y-2">
                                <Button
                                    onClick={() => navigate("/register")}
                                    variant="outline"
                                    className="w-full"
                                >
                                    Register Again
                                </Button>
                                <Button
                                    onClick={() => navigate("/login")}
                                    variant="ghost"
                                    className="w-full"
                                >
                                    Back to Login
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
}
